import { useMemo } from "react";
import store from "store";

interface CfdConfig {
  marginPrecision: number; // 保证金精度
  leverageRange: { min: number; max: number }; // 杠杆范围
  tpSlRange: { max: number; min: number }; // 止盈止损范围 [最大, 最小]
  keepFeeRatio: number; // 过夜费率
  /** 买单最大偏离度（CFD 或现货 exts） */
  marketBuyMaxDeviation?: number | string;
  /** 限价卖单最大偏离度（CFD exts） */
  limitOrderMaxDeviation?: number | string;
  /** 价格最大偏离度（CFD exts） */
  priceMaxDeviation?: number | string;
  /** 最小下单数量（CFD exts 或现货） */
  minOrderQty?: string;
  /** 最小下单金额（CFD exts 或现货） */
  minOrderAmount?: string;
  /** 最大下单数量（CFD exts 或现货） */
  maxOrderQty?: string;
  /** 挂单手续费（CFD exts 或现货） */
  pendingOrderFee?: string | number;
}

/** 从 filters 数组中按 filter 类型取第一条 */
function getFilter(cfg: any, filterName: string): Record<string, string> | undefined {
  const filters = cfg?.filters;
  if (!Array.isArray(filters)) return undefined;
  const one = filters.find((f: any) => f?.filter === filterName);
  return one ?? undefined;
}

/**
 * 获取当前CFD交易对的配置信息
 * 优先从 symbol 的 filters 取值，其次从 exts；部分项可从现货配置取（同交易对 base 的 spot symbol）
 */
const useCfdConfig = (): Partial<CfdConfig> => {
  const { name, currentConfig, config: allConfig } = store.market;

  return useMemo(() => {
    const out: Partial<CfdConfig> = {};
    const cfg = currentConfig as any;
    const exts = cfg?.exts;

    // 现货配置：当前为 CFD 时，尝试取同交易对的现货 symbol 配置（如 btc_usdt）
    const spotSymbol = name ? String(name).replace(/_cfd$/i, "") : "";
    const spotConfig = allConfig && spotSymbol ? (allConfig[spotSymbol] as any) : undefined;
    const spotExts = spotConfig?.exts;
    const spotFilters = (f: string) => getFilter(spotConfig, f);

    const getNum = (v: any): number | undefined => {
      if (v == null || v === "") return undefined;
      const n = typeof v === "number" ? v : parseFloat(v);
      return Number.isNaN(n) ? undefined : n;
    };
    const getStr = (v: any): string | undefined => {
      if (v == null || v === "") return undefined;
      const s = String(v).trim();
      return s || undefined;
    };

    // filters: PROTECTION_LIMIT -> buyMaxDeviation(限价买单), sellMaxDeviation(限价卖单); PROTECTION_MARKET -> maxDeviation(市场价)
    const protectionLimit = getFilter(cfg, "PROTECTION_LIMIT");
    const protectionMarket = getFilter(cfg, "PROTECTION_MARKET");
    const quantityFilter = getFilter(cfg, "QUANTITY");
    const quoteQtyFilter = getFilter(cfg, "QUOTE_QTY");

    const limitBuyDev =
      getNum(protectionLimit?.buyMaxDeviation) ??
      getNum(exts?.CFD_MARKET_DEVIATION ?? exts?.MARKET_ORDER_MAX_DEVIATION) ??
      getNum(spotExts?.MARKET_ORDER_MAX_DEVIATION) ?? getNum(spotFilters("PROTECTION_LIMIT")?.buyMaxDeviation);
    if (limitBuyDev !== undefined) out.marketBuyMaxDeviation = limitBuyDev;

    const limitSellDev =
      getNum(protectionLimit?.sellMaxDeviation) ??
      getNum(exts?.CFD_LIMIT_SELL_DEVIATION ?? exts?.LIMIT_ORDER_MAX_DEVIATION) ??
      getNum(spotFilters("PROTECTION_LIMIT")?.sellMaxDeviation);
    if (limitSellDev !== undefined) out.limitOrderMaxDeviation = limitSellDev;

    const marketDev =
      getNum(protectionMarket?.maxDeviation) ??
      getNum(exts?.CFD_PRICE_MAX_DEVIATION ?? exts?.MAX_DEVIATION) ??
      getNum(spotFilters("PROTECTION_MARKET")?.maxDeviation);
    if (marketDev !== undefined) out.priceMaxDeviation = marketDev;

    // filters: QUANTITY -> min, max; QUOTE_QTY -> min
    out.minOrderQty =
      getStr(quantityFilter?.min) ??
      getStr(exts?.CFD_MIN_ORDER_QTY ?? exts?.minOrderQty) ??
      getStr(spotExts?.minOrderQty) ??
      (spotConfig?.minOrderQty != null ? String(spotConfig.minOrderQty) : undefined) ??
      getStr(spotFilters("QUANTITY")?.min);
    out.maxOrderQty =
      getStr(quantityFilter?.max) ??
      getStr(exts?.CFD_MAX_ORDER_QTY ?? exts?.maxOrderQty) ??
      getStr(spotExts?.maxOrderQty) ??
      (spotConfig?.maxOrderQty != null ? String(spotConfig.maxOrderQty) : undefined) ??
      getStr(spotFilters("QUANTITY")?.max);
    out.minOrderAmount =
      getStr(quoteQtyFilter?.min) ??
      getStr(exts?.CFD_MIN_ORDER_AMOUNT ?? exts?.minOrderAmount) ??
      getStr(spotExts?.minOrderAmount) ??
      (spotConfig?.minOrderAmount != null ? String(spotConfig.minOrderAmount) : undefined) ??
      getStr(spotFilters("QUOTE_QTY")?.min);

    // 挂单手续费：CFD 或现货 exts（接口返回 MAKER_FEE_RATE，展示时 * 100 为百分比）
    const pendingFee =
      getNum(exts?.MAKER_FEE_RATE);
    if (pendingFee !== undefined) out.pendingOrderFee = pendingFee;

    if (!exts) {
      return out;
    }

    // 保证金精度
    if (exts.CFD_MARGIN_PRECISION) {
      const precision = parseInt(exts.CFD_MARGIN_PRECISION, 10);
      if (!isNaN(precision) && precision >= 0) out.marginPrecision = precision;
    }

    // 杠杆范围 "1,100" -> {min: 1, max: 100}
    if (exts.CFD_LEVERAGE_RANGE) {
      const parts = String(exts.CFD_LEVERAGE_RANGE).split(",").map((s: string) => parseInt(s.trim(), 10));
      if (parts.length === 2 && !parts.some(isNaN)) {
        out.leverageRange = {
          min: Math.min(parts[0], parts[1]),
          max: Math.max(parts[0], parts[1]),
        };
      }
    }

    // 止盈止损范围 "5,0.8" -> {max: 5, min: 0.8}
    if (exts.CFD_TP_SL_RANGE) {
      const parts = String(exts.CFD_TP_SL_RANGE).split(",").map((s: string) => parseFloat(s.trim()));
      if (parts.length === 2 && !parts.some(isNaN)) {
        out.tpSlRange = {
          max: Math.max(parts[0], parts[1]),
          min: Math.min(parts[0], parts[1]),
        };
      }
    }

    // 过夜费率
    if (exts.CFD_KEEP_FEE_RATIO) {
      const ratio = parseFloat(exts.CFD_KEEP_FEE_RATIO);
      if (!isNaN(ratio) && ratio >= 0) out.keepFeeRatio = ratio;
    }

    return out;
  }, [name, currentConfig, allConfig]);
};

export default useCfdConfig;

